import { buildCrmSeedMap } from '@hostingportal/app-boot'
import { type HostingPortalRuntime, setRuntime } from '@hostingportal/capabilities'
import { setSessionScopes } from '@hostingportal/hooks'
import { bootMockRuntime } from '@hostingportal/mock-runtime'
// The deterministic hosting.com CRM demo factories, injected into the published
// `buildCrmSeedMap(factories)` seam so the mock store boots seeded.
import { hostingFixtureFactories } from '@hostingportal/test-fixtures'
import { ensureFreshToken, installAuthenticatedFetch, setTokenRefresher, syncAuthGlobals } from './auth'
import { refresh } from './auth-provider'
import { initTheme } from './theme'

// Capability side-effect imports - each module's barrel self-registers its
// descriptors with the registry on import. The barrels are empty stubs in
// PLAN-00; the module tracks fill them. Importing them now keeps the boot wiring
// stable so a track only has to populate its folder, not touch this file.
import '@hostingportal/capabilities/contacts'
import '@hostingportal/capabilities/companies'
import '@hostingportal/capabilities/deals'
import '@hostingportal/capabilities/subscriptions'
import '@hostingportal/capabilities/activities'
import '@hostingportal/capabilities/associations'
import '@hostingportal/capabilities/tasks'
import '@hostingportal/capabilities/products'
import '@hostingportal/capabilities/sequences'
import '@hostingportal/capabilities/email'
import '@hostingportal/capabilities/engagement'
import '@hostingportal/capabilities/enrichment'
import '@hostingportal/capabilities/analytics'
import '@hostingportal/capabilities/config'
// RBAC v2 (V3 R5): teams caps power the Teams settings page + record team field.
import '@hostingportal/capabilities/teams'
// V3 R9: BYO LLM provider keys power the Model providers settings page.
import '@hostingportal/capabilities/provider-keys'
import '@hostingportal/capabilities/agent'
import '@hostingportal/capabilities/integrations'
// PLAN-09: the HubSpot→hosting.com CRM migration console capabilities.
import '@hostingportal/capabilities/migration'
// Generic bulk CSV importer (import.run_batch - the first bulk-write endpoint).
import '@hostingportal/capabilities/import'

// Register record analytics cards against the shell record-chart registry.
// This remains a stable side-effect anchor for future hosting account-metric
// cards; the previous domain-specific chart bundle is intentionally disabled.
import '../modules/analytics/register-record-charts'

// PLAN-03 (DR-17): register the agent chat panel against the shell record-panel
// registry so `?panel=agent` / `Cmd+Shift+A` mounts the real `AgentChatPanel`.
// Deliberately a DYNAMIC import (see bootRuntime below): the agent chat panel +
// its markdown/agent-card dependency graph is ~80 KB of source that must not
// ride in the entry chunk. The record-panel registry notifies the host on late
// registration, so a `?panel=agent` deep link upgrades from the placeholder
// slot to the real panel as soon as this chunk lands.

// FIX (record-open bug): the module record-panel side-effects (DR-11) for the CRM
// objects were never imported at boot, so clicking a contact/company/deal row
// set the `?panel=` URL state but no panel was registered, so nothing mounted.
// Register them all here so row-click opens the record side panel.
import '../modules/companies/register-panels'
import '../modules/contacts/register-panels'
import '../modules/deals/register-panels'
import '../modules/subscriptions/register-panels'
import '../modules/invoices/register-panels'
import '../modules/tasks/register-panels'
// Activity detail panel: makes every timeline activity row clickable/viewable.
import '../modules/activities/register-panels'
// Analytics-event detail panel ('analyticsEvent'): timeline event rows open it
// from any route, so register at boot rather than only on the events routes.
import '../modules/events/register-panels'

/**
 * Boot the runtime exactly once per page load. Selects `mock` or `live` from
 * `VITE_HOSTINGPORTAL_RUNTIME` (defaults to `live`). In mock mode, seeds the in-memory
 * store from the shared deterministic builder (`@hostingportal/app-boot`) and grants the
 * dev session all scopes.
 *
 * See docs/01-foundations/mock-runtime.md.
 */
export function bootRuntime(): void {
  const mode = (import.meta.env.VITE_HOSTINGPORTAL_RUNTIME ?? 'live') as HostingPortalRuntime
  setRuntime(mode)
  initTheme()
  installAuthenticatedFetch()
  syncAuthGlobals()

  // Agent chat panel registration - async on purpose (see the comment at the
  // top of this file). Fire-and-forget: the registry re-renders the host when
  // the chunk lands.
  void import('../modules/agent-workspace/register-panels')

  // Persistent logins: register the provider's silent token refresher, warm the
  // token on boot, and re-check periodically so the session survives reloads and
  // idle time for as long as the refresh token is valid (days/weeks, not ~1h).
  setTokenRefresher(refresh)
  void ensureFreshToken()
  if (typeof window !== 'undefined') {
    window.setInterval(() => void ensureFreshToken(), 30 * 60 * 1000)
  }

  if (mode === 'mock') {
    bootMockRuntime({ seeds: buildCrmSeedMap(hostingFixtureFactories) })
    // Dev session has every scope. PLAN-02 swaps this for the Cognito JWT's
    // workspace_id + role → scopes in live mode.
    setSessionScopes(['*'])
  }
}
